import { Form, Formik, FormikProps } from 'formik';
import { useCallback, useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import { FormPart } from '@modules/shared/components/FormPart';
import { queryParams } from '@shared/commonFunctions';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { Modal } from '@shared/components/modals/Modal';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { TextInputField } from '@shared/fields/TextInputField';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { ModalWrapper } from '@styles/StyledDiv';

import { IEvent } from '@shared/inputs/NumberInput';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { IEditRetail } from '../../../interfaces';
import { productRetailValidationSchema } from '../constants';
import { requestUpdateProduct, updateCalculatorProduct } from '../queries';
import { IRetailProps, ISelectedRetailToEdit } from '../interfaces';

const handleChange = async (event: IEvent, props: FormikProps<ISelectedRetailToEdit>) => {
  await props.setFieldValue?.('retail', typeof event.target.value === 'number' ? event.target.value : null);
  await props.setFieldValue?.('markup', event.target.value - (props?.values?.cost ?? 0));
};

const formConfig = (props: FormikProps<ISelectedRetailToEdit>) => [
  {
    width: '100%',
    name: 'retail',
    component: NumberInputField,
    decimalScale: 2,
    prefix: '$',
    onChange: (target: IEvent) => handleChange(target, props),
  },
  {
    width: '100%',
    name: 'reason',
    multiline: true,
    maxRows: 5,
    component: TextInputField,
  },
];

export const EditRetailModal = ({
  isEditRetailModalOpen,
  setEditRetailModalOpen,
  selectedRetail,
  mutateDealProducts,
  customData,
  setChangesMade,
  productRules,
  ...props
}: IRetailProps) => {
  if (!isEditRetailModalOpen || !selectedRetail) return null;
  const params = queryParams();
  const dealId = params.dealId;

  const [loading, setLoading] = useState(false);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [pendingData, setPendingData] = useState<ISelectedRetailToEdit | null>(null);
  const [alertText, setAlertText] = useState('');
  const [alertButtonText, setAlertButtonText] = useState('Proceed');

  const { getFlVscMarkupAlertText, isFlVscMarkupInvalid, contactState } = props;
  
  const intialValues = useMemo(() => (
    {
      ...selectedRetail,
      retail: selectedRetail?.retail || null,
      reason: selectedRetail?.reason,
    }
  ), [selectedRetail]);

  const productRule = productRules?.find(pr => pr.productName === selectedRetail?.productTypeName);

  const processSave = useCallback(async (data: ISelectedRetailToEdit) => {
    setChangesMade(true);
    setEditRetailModalOpen(0);
    setLoading(true);
    try {
      customData?.type === 'dealCalculator' ?
        await updateCalculatorProduct(data as IEditRetail, customData?.data?.leadId || '', data.dealCalculatorLeadProductId || '')
        : await requestUpdateProduct(data, dealId);

      mutateDealProducts();
    } finally {
      setLoading(false);
    }
  }, [dealId, customData, setChangesMade, setEditRetailModalOpen, mutateDealProducts]);

  const setOpenMarkupAlertModal = useCallback((buttonText: string, alertText: string) => {
        setAlertButtonText(buttonText);
        setAlertText(alertText);
        setOpenAlertModal(true);
  }, []);

  const handleSubmit = useCallback(async (data: ISelectedRetailToEdit) => {
    if (isFlVscMarkupInvalid(productRule?.productName ?? '', data.markup, contactState)) {
      setOpenMarkupAlertModal('Ok', getFlVscMarkupAlertText());
      return;
    }
    const maxAllowedRetail = productRule?.providers?.[0]?.maxAllowedRetail;
    if (productRule && maxAllowedRetail && data.retail > maxAllowedRetail) {
      setOpenMarkupAlertModal('Proceed', getFlVscMarkupAlertText(maxAllowedRetail));
      setPendingData(data);
      return; 
    }
    
    await processSave(data);
  }, [productRule, processSave, isFlVscMarkupInvalid, contactState, getFlVscMarkupAlertText]);

  const confirmationOfRetailMaxAlert = useCallback(async () => {
    setOpenAlertModal(false);
    if (pendingData) {
      await processSave(pendingData);
      setPendingData(null);
    }
  }, [pendingData, processSave]);

  return (
    <>
      <Modal
        title="Edit Retail"
        dialogWidth={'500px'}
        open={!!isEditRetailModalOpen}
        resetPosition={isEditRetailModalOpen}
        onClose={() => setEditRetailModalOpen(0)}
      >
        <Formik
          enableReinitialize
          initialValues={intialValues as ISelectedRetailToEdit}
          validationSchema={productRetailValidationSchema}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit}>
              <ModalWrapper>
                {productRule && (
                  <Typography style={{ marginBottom: '8px', fontSize: '14px', color: 'black' }}>
                    {productRule.productTypeRuleDescription}
                  </Typography>
                )}
                <FormPart config={formConfig(props as FormikProps<ISelectedRetailToEdit>) as IFormFieldConfig<typeof props>[]} />
                <CommonButtonWrapper direction="row">
                  <CancelButton onClick={() => setEditRetailModalOpen(0)} />
                  <SaveButton isLoading={loading} />
                </CommonButtonWrapper>
              </ModalWrapper>
            </Form>
          )}
        </Formik>
      </Modal>
      <AlertModal
        title="Alert"
        textSize="20px"
        subtitle={alertText}
        cancelButtonText="Cancel"
        buttonText={alertButtonText}
        marginTop={0}
        open={openAlertModal}
        onConfirm={confirmationOfRetailMaxAlert}
        onClose={() => {
          setOpenAlertModal(false);
          setPendingData(null);
        }}
      />
    </>
  );
};